import { selectSiteHasFeature } from "../featureFlags/SitesFeatureFlagsStore";
import { useSessionDispatch as useDispatch } from "../../common/Hooks/useSessionDispatch";
import { FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { DiffuseOptimizationIcons, IconCategory } from "../../SolarGikLib/icons/IconsModels";
import getIcons from "../../SolarGikLib/icons/Icons";
import { RootState, AppDispatch } from "../app/Store";
import { getDiffuseOptimizationStatus } from "./DiffuseOptimizationStore";
import { repetitivePolling } from "../../common/AsyncUtils";
import APP_CONFIG from "../app/configuration/AppConfig";
import DarkTooltip from "../../SolarGikLib/tooltip/DarkTooltip";
import { selectFieldState } from "../app/store/MultisiteTagsStore";
import { FieldState } from "../data_point/models/TagsModels";
import { FeatureFlag } from "../featureFlags/FeatureFlagsModels";

interface IDiffuseOptimizationStatusProps {
  siteId: string;
}

const DiffuseOptimizationIcon = getIcons(
  DiffuseOptimizationIcons.DiffuseOptimizationOn,
  IconCategory.DiffuseOptimization
);

const DiffuseOptimizationStatus: FC<IDiffuseOptimizationStatusProps> = ({
  siteId,
}: IDiffuseOptimizationStatusProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const optimization = useSelector((state: RootState) => state.diffuseOptimization);
  const isDiffuseOptimizationOn = optimization.siteId === siteId && optimization.isDiffuseOptimizationOn;
  const isDiffuseOptimizationEnabled = useSelector(
    (state: RootState) => selectSiteHasFeature(state, siteId, FeatureFlag.DiffuseOptimization) ?? false
  );
  const fieldState = useSelector((state: RootState) => selectFieldState(state, siteId));

  useEffect(() => {
    if (!isDiffuseOptimizationEnabled) {
      return;
    }

    const controller = new AbortController();
    let request: ReturnType<ReturnType<typeof getDiffuseOptimizationStatus>> | undefined;
    repetitivePolling(
      "diffuseOptimization",
      async () => {
        request = dispatch(getDiffuseOptimizationStatus(siteId));
        await request.unwrap();
      },
      APP_CONFIG.milliSecBetweenGetDiffuseOptimizationStatus,
      APP_CONFIG.milliSecBetweenFailedRequests,
      controller.signal
    );

    return () => {
      controller.abort();
      request?.abort();
    };
  }, [dispatch, isDiffuseOptimizationEnabled, siteId]);

  if (!(
    isDiffuseOptimizationEnabled &&
    isDiffuseOptimizationOn &&
    fieldState.value === FieldState.Tracking
  )) {
    return null;
  }

  return (
    <DarkTooltip
      title={
        optimization.error
          ? "Diffuse optimization was active at the last successful check; status could not be refreshed"
          : "Diffuse Irradiance Optimization Active"
      }
    >
      <div>
        <DiffuseOptimizationIcon />
      </div>
    </DarkTooltip>
  );
};

export default DiffuseOptimizationStatus;
